import { useCallback, useRef, useState } from 'react';
import { useFileDropzone } from '../FileDropzone';
import { IMAGE_DIALOG_TYPES } from '../constants';
import { ACCEPTED_FILE_FORMATS, MAX_IMAGE_COUNT } from './constants';

export const useImageManager = ({ images, dialogType }) => {
  const [currentImages, setCurrentImages] = useState(images);
  const uploadPhotosRef = useRef({});

  const acceptedFileFormats = ACCEPTED_FILE_FORMATS[dialogType];
  const maxImageCount = MAX_IMAGE_COUNT[dialogType];
  const maxFileCount = maxImageCount - currentImages.length;

  const {
    deleteImageFile,
    dropFrameRef,
    clearFileValidationErrors,
    clearFileValidationError,
    fileInputRef,
    fileValidationErrors,
    handleFile,
    imageFiles,
    maxDropFileCount,
    resetImageFiles,
  } = useFileDropzone({
    acceptedFileFormats,
    dialogType,
    maxFileCount,
  });

  const uploadRefCallback = (fileUrl) => (element) => {
    uploadPhotosRef.current[fileUrl] = element;
  };

  const hasLogoValidationErrors =
    dialogType === IMAGE_DIALOG_TYPES.LOGO && fileValidationErrors?.length;

  const deleteCurrentImage = (imageUrl) => {
    const updatedImages = currentImages.filter(
      (image) => image.url !== imageUrl,
    );
    setCurrentImages(updatedImages);
  };

  const clearDropCardErrorMessage = () => {
    if (hasLogoValidationErrors) {
      clearFileValidationErrors();
    }
  };

  const getDropCardErrorMessage = () => {
    if (hasLogoValidationErrors) {
      return fileValidationErrors[0].errorMessage;
    }
  };

  const onImageUpload = (fileUrl) => (uploadedImageData) => {
    setCurrentImages((prevState) => [...prevState, uploadedImageData]);
    deleteImageFile(fileUrl);
  };

  const updateCurrentImageDescription = useCallback(
    (url) => (description) => {
      setCurrentImages((prevState) => {
        const updatedPhotos = prevState.map((photoData) => {
          if (photoData.url === url) {
            return { ...photoData, description };
          }
          return photoData;
        });

        return updatedPhotos;
      });
    },
    [],
  );

  const handleAddPhotosOnClick = () => {
    if (fileValidationErrors?.length) {
      clearFileValidationErrors();
    }

    imageFiles.forEach(({ fileUrl }) => {
      const { uploadImage } = uploadPhotosRef.current[fileUrl];
      uploadImage();
    });

    uploadPhotosRef.current = {};
  };

  const abortAllUploads = () => {
    imageFiles?.forEach(({ fileUrl }) => {
      const { abortImageUpload } = uploadPhotosRef.current[fileUrl];
      abortImageUpload();
    });
  };

  const handleDropTargetClick = () => {
    clearDropCardErrorMessage();
    fileInputRef.current.click();
  };

  const getHasImagesChanged = () => {
    if (currentImages.length !== images.length) {
      return true;
    }

    const currentImageMap = currentImages.reduce((imageMap, image) => {
      imageMap[image.url] = image;
      return imageMap;
    }, {});

    const hasImagesChanged = images.some((image) => {
      const currentImage = currentImageMap[image.url];
      if (!currentImage) {
        return true;
      }

      const currentDescription = currentImage.description || '';
      const imageDescription = image.description || '';
      return currentDescription !== imageDescription;
    });

    return hasImagesChanged;
  };

  const isSavedButtonDisabled =
    (!images.length && !currentImages.length) || !getHasImagesChanged();

  return {
    abortAllUploads,
    acceptedFileFormats,
    clearDropCardErrorMessage,
    clearFileValidationError,
    currentImages,
    deleteCurrentImage,
    deleteImageFile,
    dropCardErrorMessage: getDropCardErrorMessage(),
    dropFrameRef,
    fileInputRef,
    fileValidationErrors,
    handleAddPhotosOnClick,
    handleFile,
    handleDropTargetClick,
    imageFiles,
    isSavedButtonDisabled,
    maxDropFileCount,
    onImageUpload,
    resetImageFiles,
    updateCurrentImageDescription,
    uploadRefCallback,
  };
};
